import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { usePopover } from '@calendly/ui/components/popover';
import { Sheet } from '@calendly/ui/components/sheet';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { mergeRefs } from '@calendly/ui/utils/merge-refs';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { setFilterMenuTriggerElement } from 'hooks/filter_menu_container_registry';
import { computePortaledMenuPosition, getEstimatedMenuWidth, getPortaledMenuCollisionBounds, resolvePortaledMenuPlacement } from 'hooks/use_portaled_menu_position';
import { useFilterMenu } from './filter_menu_context';
import { FilterMenuProtectedPopoverContent } from './filter_menu_popover_content';
import { useFilterMenuScrollBehavior } from './filter_menu_scroll_behavior_context';
const parseOverlayVerticalPlacement = raw => {
  if (!raw) return null;
  if (raw.startsWith('top')) return 'top';
  if (raw.startsWith('bottom')) return 'bottom';
  return null;
};
const contentDefaultMinWidthCss = "c1xgrv1u";
const contentPullLeftCss = "ctfqccj";
const contentComfortableMinWidthCss = "c11mmz5d";
const contentContentMinWidthCss = "c1gediig";
const contentTabletDownCss = "c1mjpp1x";
const contentPhoneCss = "c12kfw02";
const contentSeamBridgeBelowCss = "c9u7oly";
const contentSeamBridgeAboveCss = "c1a8qzmd";
const contentSurfaceCss = "cg2j3zt";
const contentShellCss = "cc7u4rr";
const contentSeamBridgeCss = "c1clnryq";
const contentInnerLayoutCss = "cnqk7n5";
const contentScrollCss = "c15g3r0y";

/** RAC anchor only — visible panel is positioned on the inner FixedContainer. */
const popoverAnchorPassthroughCss = "p128h4pg";

/**
 * CUI FilterMenuContent layout with protected interact-outside for nested field controls.
 */
export const FilterMenuContentShell = forwardRef(({
  body,
  footer,
  placement,
  offset,
  pullLeft,
  className,
  contentSurfaceRef,
  onKeyDown,
  ...props
}, ref) => {
  const context = useFilterMenu();
  const {
    closeOnPageScroll,
    pullLeft: contextPullLeft
  } = useFilterMenuScrollBehavior();
  const {
    triggerRef
  } = usePopover();
  const isTabletDown = useDeviceType('isTabletDown');
  const isSmallPhone = useDeviceType('isSmallPhone');
  const popoverAnchorRef = useRef(null);
  const popoverShellRef = useRef(null);
  const lockedHorizontalPlacementRef = useRef(null);
  const usesFixedShell = !closeOnPageScroll;
  const {
    isOpen,
    overlayVerticalPlacement,
    setOverlayVerticalPlacement
  } = context;

  /**
   * Clears runtime layout state applied during the open layout effect's sync pass.
   *
   * Seam-bridge CSS variables and fixed positioning are written as inline styles and
   * data attributes because they depend on live trigger geometry from
   * `getBoundingClientRect()`. Linaria cannot express those measurements, so we reset
   * them here when the menu closes or the trigger is unavailable to avoid stale layout
   * leaking across opens.
   */
  const clearSeamTriggerVars = useCallback(shell => {
    shell?.style.removeProperty('--filter-menu-trigger-width');
    shell?.style.removeProperty('--filter-menu-trigger-offset-x');
    shell?.removeAttribute('data-trigger-left-aligned');
    shell?.removeAttribute('data-trigger-right-aligned');
    shell?.removeAttribute('data-pull-left');
    if (usesFixedShell) {
      shell?.style.removeProperty('top');
      shell?.style.removeProperty('left');
      return;
    }
    shell?.style.removeProperty('position');
    shell?.style.removeProperty('top');
    shell?.style.removeProperty('left');
    shell?.style.removeProperty('transform');
  }, [usesFixedShell]);
  const opensAbove = overlayVerticalPlacement === 'top';
  useLayoutEffect(() => {
    const shell = popoverShellRef.current;
    if (!shell || !isOpen) return undefined;
    const sync = () => {
      const placementNode = usesFixedShell ? popoverAnchorRef.current : shell;
      const dataPlacement = placementNode?.getAttribute('data-placement');
      setOverlayVerticalPlacement(parseOverlayVerticalPlacement(dataPlacement));
      const trigger = triggerRef?.current;
      if (!trigger) {
        clearSeamTriggerVars(shell);
        return;
      }
      const triggerRect = trigger.getBoundingClientRect();
      const explicitPullLeft = pullLeft ?? contextPullLeft;
      if (usesFixedShell) {
        const menuWidth = getEstimatedMenuWidth(shell, context.contentMinWidth);
        const menuHeight = shell.offsetHeight;
        const bounds = getPortaledMenuCollisionBounds(trigger);
        const menuGap = offset ?? context.offset ?? 0;
        const horizontalPlacement = lockedHorizontalPlacementRef.current ?? resolvePortaledMenuPlacement({
          dataPlacement: null,
          contextPlacement: context.placement,
          pullLeft: explicitPullLeft,
          anchorRect: triggerRect,
          menuWidth,
          bounds
        });
        if (!lockedHorizontalPlacementRef.current) {
          lockedHorizontalPlacementRef.current = horizontalPlacement;
        }
        const {
          top,
          left
        } = computePortaledMenuPosition({
          anchorRect: triggerRect,
          menuWidth,
          menuHeight,
          bounds,
          placement: horizontalPlacement,
          gap: menuGap
        });
        shell.style.top = `${top}px`;
        shell.style.left = `${left}px`;
        shell.toggleAttribute('data-pull-left', horizontalPlacement === 'bottom-end');
      }
      const shellRect = shell.getBoundingClientRect();
      const triggerOffsetX = triggerRect.left - shellRect.left;
      const triggerRightOffset = shellRect.right - triggerRect.right;
      shell.style.setProperty('--filter-menu-trigger-width', `${triggerRect.width}px`);
      shell.style.setProperty('--filter-menu-trigger-offset-x', `${triggerOffsetX}px`);
      shell.toggleAttribute('data-trigger-left-aligned', Math.abs(triggerOffsetX) < 1);
      shell.toggleAttribute('data-trigger-right-aligned', Math.abs(triggerRightOffset) < 1);
      if (!usesFixedShell) {
        shell.toggleAttribute('data-pull-left', Boolean(explicitPullLeft));
      }
    };
    sync();
    const placementTarget = usesFixedShell ? popoverAnchorRef.current : shell;
    let placementObserver;
    if (placementTarget) {
      placementObserver = new MutationObserver(sync);
      placementObserver.observe(placementTarget, {
        attributes: true,
        attributeFilter: ['data-placement']
      });
    }
    const resizeObserver = new ResizeObserver(sync);
    resizeObserver.observe(shell);
    if (triggerRef?.current) {
      resizeObserver.observe(triggerRef.current);
    }
    window.addEventListener('resize', sync);
    if (usesFixedShell) {
      window.addEventListener('scroll', sync, true);
    }
    return () => {
      placementObserver?.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('resize', sync);
      if (usesFixedShell) {
        window.removeEventListener('scroll', sync, true);
      }
    };
  }, [clearSeamTriggerVars, context.contentMinWidth, context.offset, context.placement, contextPullLeft, isOpen, offset, pullLeft, setOverlayVerticalPlacement, triggerRef, usesFixedShell]);
  useEffect(() => {
    if (!isOpen) {
      lockedHorizontalPlacementRef.current = null;
      clearSeamTriggerVars(popoverShellRef.current);
    }
  }, [clearSeamTriggerVars, isOpen]);
  useLayoutEffect(() => {
    if (!isOpen) {
      setFilterMenuTriggerElement(null);
      return undefined;
    }
    setFilterMenuTriggerElement(triggerRef?.current ?? null);
    return () => {
      setFilterMenuTriggerElement(null);
    };
  }, [isOpen, triggerRef]);
  const shellClassName = classNames(contentShellCss, {
    [contentDefaultMinWidthCss]: context.contentMinWidth === 'default',
    [contentComfortableMinWidthCss]: context.contentMinWidth === 'comfortable',
    [contentContentMinWidthCss]: context.contentMinWidth === 'content',
    [contentPullLeftCss]: pullLeft ?? contextPullLeft,
    [contentTabletDownCss]: context.responsive && isTabletDown && context.contentMinWidth !== 'content',
    [contentPhoneCss]: context.responsive && isSmallPhone
  }, className);
  const panelContent = <>
      <span aria-hidden className={classNames(contentSeamBridgeCss, {
      [contentSeamBridgeBelowCss]: !opensAbove,
      [contentSeamBridgeAboveCss]: opensAbove
    })} />
      <Sheet ref={contentSurfaceRef} className={contentSurfaceCss}>
        <div className={contentInnerLayoutCss}>
          <div className={contentScrollCss}>{body}</div>
          {footer}
        </div>
      </Sheet>
    </>;
  return <FilterMenuProtectedPopoverContent {...props} ref={usesFixedShell ? mergeRefs(ref, popoverAnchorRef) : mergeRefs(ref, popoverShellRef)} onKeyDown={onKeyDown} placement={placement ?? context.placement} offset={offset ?? context.offset} isNonModal className={usesFixedShell ? popoverAnchorPassthroughCss : shellClassName}>
      {usesFixedShell ? <FixedContainer ref={popoverShellRef} fixedWhen className={shellClassName}>
          {panelContent}
        </FixedContainer> : panelContent}
    </FilterMenuProtectedPopoverContent>;
});

require("./filter_menu_content_shell.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./filter_menu_content_shell.js");